import React, { createContext, useContext, useLayoutEffect, useMemo, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import { fitPatternQuestionScale, patternSizePolicy, PATTERN_SIZE_READABILITY } from './patternSizePolicy';

export const PatternSizeContext = createContext({ enabled: false });
export const usePatternSize = () => useContext(PatternSizeContext);

export function usePatternQuestionScale(question) {
  const rootRef = useRef(null);
  const policy = useMemo(() => patternSizePolicy(question), [question]);
  const [fit, setFit] = useState({ scale: 1, padding: 6, gap: 4, wrap: false });
  useLayoutEffect(() => {
    if (!policy.enabled || !rootRef.current) return undefined;
    const root = rootRef.current;
    let frame;
    const measure = () => {
      const rows = [...root.querySelectorAll('[data-pattern-size-row]')].map((row) => ({
        width: row.clientWidth,
        count: Number(row.dataset.patternSizeCount),
        connectors: Number(row.dataset.patternSizeConnectors || 0),
      }));
      root.querySelectorAll('[data-pattern-size-container]').forEach((container) => {
        if (!container.querySelector('[data-pattern-size-row]')) {
          const style = getComputedStyle(container);
          rows.push({
            width: container.clientWidth - parseFloat(style.paddingLeft || 0) - parseFloat(style.paddingRight || 0),
            count: Number(container.querySelector('[data-pattern-size-grid-columns]')?.dataset.patternSizeGridColumns)
              || container.querySelectorAll('[data-pattern-size-item]').length,
            connectors: 0,
          });
        }
      });
      const cap = root.clientWidth >= PATTERN_SIZE_READABILITY.largeDisplayWidth
        ? PATTERN_SIZE_READABILITY.largeDisplayCap : PATTERN_SIZE_READABILITY.mobileCap;
      const next = fitPatternQuestionScale(rows, policy, cap);
      setFit((previous) => (
        Math.abs(previous.scale - next.scale) < 0.0001 && previous.padding === next.padding
          && previous.gap === next.gap && previous.wrap === next.wrap ? previous : next
      ));
    };
    measure();
    const schedule = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(measure);
    };
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(schedule);
    observer?.observe(root);
    root.querySelectorAll('[data-pattern-size-row], [data-pattern-size-container]').forEach((node) => observer?.observe(node));
    window.addEventListener('resize', schedule);
    return () => {
      observer?.disconnect();
      cancelAnimationFrame(frame);
      window.removeEventListener('resize', schedule);
    };
  }, [policy]);
  return { ...policy, ...fit, rootRef, slot: (policy.maximumSize + fit.padding) * fit.scale };
}

// Scale the visual slots, not the answer button/touch target. An arrow stays
// attached to the following item when a row wraps, preserving step order.
export function PatternSizeRow({ items, connector = '' }) {
  const size = usePatternSize();
  const rowRef = useRef(null);
  const [width, setWidth] = useState(0);
  useLayoutEffect(() => {
    const row = rowRef.current;
    const measure = () => setWidth(row.clientWidth);
    measure();
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure);
    observer?.observe(row);
    return () => observer?.disconnect();
  }, []);
  const gap = size.gap * size.scale;
  const unit = size.slot + (connector ? PATTERN_SIZE_READABILITY.connectorPx * size.scale : 0) + gap;
  const capacity = Math.max(1, Math.min(PATTERN_SIZE_READABILITY.maxColumns,
    size.wrap && width ? Math.floor((width - PATTERN_SIZE_READABILITY.overflowBufferPx) / unit) : items.length));
  const columns = Math.ceil(items.length / Math.ceil(items.length / capacity)) || 1;
  const rows = Array.from({ length: Math.ceil(items.length / columns) }, (_, index) => (
    items.slice(index * columns, (index + 1) * columns)
  ));
  return (
    <Box
      ref={rowRef}
      component="span"
      data-pattern-size-row="true"
      data-pattern-size-count={items.length}
      data-pattern-size-connectors={connector ? Math.max(0, items.length - 1) : 0}
      data-pattern-size-rows={rows.length}
      sx={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', width: '100%', minWidth: 0, gap: `${gap}px` }}
    >
      {rows.map((row, rowIndex) => (
        <Box key={rowIndex} component="span" sx={{ display: 'flex', minWidth: 0, justifyContent: 'center', alignItems: 'center', gap: `${gap}px` }}>
          {row.map((item, itemIndex) => (
            <Box key={itemIndex} component="span" sx={{ display: 'inline-flex', alignItems: 'center', flexShrink: 0 }}>
              {rowIndex * columns + itemIndex > 0 && connector ? <Box component="span" aria-hidden="true" sx={{ width: `${PATTERN_SIZE_READABILITY.connectorPx * size.scale}px`, color: '#a78bfa', fontWeight: 900, textAlign: 'center', fontSize: `${16 * size.scale}px` }}>{connector}</Box> : null}
              {item}
            </Box>
          ))}
        </Box>
      ))}
    </Box>
  );
}
